/**
 * Avatar / Image URL Builders
 *
 * All three image endpoints return an existence boolean alongside list data
 * (users: avatar, contacts: avatar, medications: image) so no HEAD-checking
 * is needed. Pass the boolean from your Redux record and a null is returned
 * immediately when the image doesn't exist, avoiding any network request.
 *
 * Response-level caching is handled by Cache-Control: public, max-age=3600
 * headers set on each backend endpoint — the browser (and any CDN) handles it.
 */

const _buildUrl = (path) => {
  const stage = process.env.REACT_APP_STAGE || 'development';
  const base = process.env.REACT_APP_API_BASE;
  return `https://${stage}-api.${base}/client/${path}`;
};

// ─── User avatars ─────────────────────────────────────────────────────────────

/**
 * @param {string} cognito_id
 * @param {string} [updated_at]  - cache-buster; use user.updated_at
 * @param {boolean} [hasAvatar]  - pass user.avatar from Redux; returns null when false
 */
export const buildAvatarUrl = (cognito_id, updated_at, hasAvatar = true) => {
  if (!cognito_id || !hasAvatar) return null;
  const ts = updated_at ? `?${updated_at}` : '';
  return _buildUrl(`users/get-user-avatar/${cognito_id}${ts}`);
};

/**
 * Clear ReactAvatar's localStorage cache of failing URLs.
 * Covers all image types (user avatars, contact avatars, medication images)
 * since they all go through ReactAvatar. Call after any successful image upload.
 */
export const clearAvatarCache = () => {
  try {
    localStorage.removeItem('react-avatar/failing');
  } catch (_) {
    // Ignore (e.g. incognito mode)
  }
};

// ─── Contact avatars ──────────────────────────────────────────────────────────

/**
 * @param {number|string} id
 * @param {string} [updated_at]  - cache-buster; use contact.updated_at
 * @param {boolean} [hasAvatar]  - pass contact.avatar from Redux; returns null when false
 */
export const getContactAvatarUrl = (id, updated_at, hasAvatar = true) => {
  if (!id || !hasAvatar) return null;
  const ts = updated_at ? `?${updated_at}` : '';
  return _buildUrl(`contacts/get-contact-avatar/${id}${ts}`);
};

export const getCachedContactAvatarUrl = getContactAvatarUrl;

// ─── Medication images ────────────────────────────────────────────────────────

/**
 * @param {number|string} id
 * @param {string} [updated_at]  - cache-buster; use medication.updated_at
 * @param {boolean} [hasImage]   - pass medication.image from Redux; returns null when false
 */
export const getMedicationImageUrl = (id, updated_at, hasImage = true) => {
  if (!id || !hasImage) return null;
  const ts = updated_at ? `?${updated_at}` : '';
  return _buildUrl(`medications/get-medication-image/${id}${ts}`);
};

export const getCachedMedicationImageUrl = getMedicationImageUrl;
